import React, { useState, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { useWindowSize, Size } from 'hooks/useWindowSize'
import {
  CreatePasswordValidationType,
  ConfirmPasswordValidationType,
  createPasswordValidationTypeInitial,
  confirmPasswordValidationTypeInitial,
  CurrentPasswordValidationType,
  currentPasswordValidationTypeInitial
} from 'share/utils/passwordUtils'
import './styles.scss'

import {
  CHANGE_PASSWORD,
  ChangePasswordVariables,
  ChangePasswordResponse,
  ChangePasswordResponseData
} from 'share/mutations/account'
import GoBackHeader from 'components/organisms/GoBackHeader'
import UserProfileResetPasswordForm from 'components/organisms/UserProfileResetPasswordForm'
import { Col, Row } from 'react-bootstrap'

interface Props {
  nextPage(page: string): void
  previousPage(page: string): void
}

const UserProfileResetPassword = (props: Props): JSX.Element => {
  const { nextPage } = props
  const [placement, setPlacement] = useState<'right' | 'top'>('right')
  const [createPasswordValidationResult, setCreatePasswordValidationResult] =
    useState<CreatePasswordValidationType>(() =>
      createPasswordValidationTypeInitial()
    )

  const [confirmPasswordValidationResult, setConfirmPasswordValidationResult] =
    useState<ConfirmPasswordValidationType>(() =>
      confirmPasswordValidationTypeInitial()
    )

  const [currentPasswordValidationResult, setCurrentPasswordValidationResult] =
    useState<CurrentPasswordValidationType>(() =>
      currentPasswordValidationTypeInitial()
    )

  const size: Size = useWindowSize()

  const [changePassword] =
    useMutation<ChangePasswordResponse, ChangePasswordVariables>(
      CHANGE_PASSWORD
    )

  useEffect(() => {
    if (size.width && size.width < 768) {
      setPlacement('top')
    } else {
      setPlacement('right')
    }
  }, [size])

  async function changePasswordHelper(
    input: ChangePasswordVariables
  ): Promise<ChangePasswordResponseData> {
    const result: ChangePasswordResponseData = {
      success: false,
      error: null
    }

    try {
      const changePasswordResult = await changePassword({
        variables: input
      })
      if (changePasswordResult.data?.authentication.changePassword) {
        result.success = true
      } else {
        result.success = false
        result.error = 'Reset password had an error. Please try again.'
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      result.success = false
      if (e?.message === 'Incorrect credentials') {
        result.error = 'Incorrect credentials'
      } else {
        result.error = 'Reset password had an error. Please try again.'
      }
    }
    return result
  }

  return (
    <React.Fragment>
      <GoBackHeader
        text='Reset Password'
        onClick={() => props.previousPage('edit')}
      />
      <div className='ui-reset-pwd-page px-4 mt-4 mt-lg-5'>
        <Row className='justify-content-md-center'>
          <Col xs lg={8}>
            <h2>Reset your password</h2>
            <p>
              Keep your information safe and sound. We recommend that you
              memorize it and don't write it down.
            </p>
          </Col>
        </Row>
        <div className='mt-4'>
          <UserProfileResetPasswordForm
            placement={placement}
            currentPasswodValidationResult={currentPasswordValidationResult}
            setCurrentPasswodValidationResult={
              setCurrentPasswordValidationResult
            }
            createPasswordValidationResult={createPasswordValidationResult}
            setCreatePasswordValidationResult={
              setCreatePasswordValidationResult
            }
            confirmPasswordValidationResult={confirmPasswordValidationResult}
            setConfirmPasswordValidationResult={
              setConfirmPasswordValidationResult
            }
            nextPage={nextPage}
            changePasswordHelper={changePasswordHelper}
          />
        </div>
      </div>
    </React.Fragment>
  )
}

export default UserProfileResetPassword
